(() => {
  angular
    .module('app.campaign')
    .factory('CampaignManagerService', CampaignManagerService);

  CampaignManagerService.$inject = [
    '$filter',
    '$location',
    'Label',
    'ModalService',
    'CampaignService',
    'CountriesService',
    'CustomSegmentsService',
    'QualityMetricsService',
  ];

  function CampaignManagerService(
    $filter,
    $location,
    Label,
    ModalService,
    CampaignService,
    CountriesService,
    CustomSegmentsService,
    QualityMetricsService,
  ) {
    let campaign;
    let originalCampaign;

    const isCampaignChanged = () => !angular.equals(campaign, originalCampaign);
    const isCampaignFieldChanged = field => originalCampaign && !angular.equals(campaign[field], originalCampaign[field]);
    const getDirtyCriteria = () => {
      const dirty = {};
      if (campaign) {
        const edit = campaign.target.criteria;
        const criterion = campaign.target.default;
        for (let key in criterion) {
          if (edit[key] && !_(criterion[key].sort()).isEqual(edit[key].sort())) {
            dirty[key] = true;
          }
        }
      }
      return dirty;
    }

    const getDirtyTargets = () => {
      const dirty = {};
      if (campaign) {
        const origin = campaign.customSegments;
        const target = campaign.targetSegment;

        origin.forEach(segment => {
          const key = segment.csid;
          if (!target[key] || target[key].length < segment.valueLabels.data.length) {
            dirty[key] = true;
          }
        });
      }
      return dirty;
    }

    let campaignPromise;
    const labelsPromise = Label.query().$promise;
    const countriesPromise = CountriesService.getSupported();
    const customSegmentsPromise = CustomSegmentsService.getPaginatedCustomSegments('allowed', 0, 1000, 'provider', true);

    const getCampaignPromise = (id, generateNewTrackpoints) => {
      if (angular.isUndefined(campaignPromise)) {
        if (id && id !== 'create') {
          campaignPromise = CampaignService.get({ id }, function (rawCampaign) {
            originalCampaign = angular.copy(rawCampaign);
            // If this is a "create duplicate campaign with new trackpoints" page:
            if (generateNewTrackpoints && rawCampaign.trackpoints) {
              // Generate new trackpoint tailcodes if necessary
              // Note that report items with the same cid+tailcode should still have the same cid+tailcode,
              // to ensure that funnel semantics are preserved
              var oldToNewTrackpointsMapping = {};
              angular.forEach(rawCampaign.trackpoints, function (trackpoint) {
                var tcode = `${trackpoint.cid}:${trackpoint.tailcode}`;
                if (oldToNewTrackpointsMapping.hasOwnProperty(tcode)) {
                  // We have already generated a new tailcode for this cid+tailcode combination, so we reuse that one
                  trackpoint.tailcode = oldToNewTrackpointsMapping[tcode];
                } else {
                  // We have not seen the reportItem's cid+tailcode combination before, so we generate a new tailcode
                  // and store the mapping in case we see the combination again
                  var newTailcode = angular.uniqueId();
                  trackpoint.tailcode = newTailcode;
                  oldToNewTrackpointsMapping[tcode] = newTailcode;
                }
              }, this);
            }
            campaign = angular.copy(rawCampaign);
          }).$promise;
        } else {
          campaignPromise = CampaignService.empty(rawCampaign => {
            originalCampaign = angular.copy(rawCampaign);
            campaign = angular.copy(rawCampaign);
          }).$promise;
        }
      }

      return campaignPromise;
    }

    function availableQualityMetrics(cid, mode) {
      let metrics = [];

      return QualityMetricsService.getAvailableQualityMetrics()
        .then(response => {
          if (response.error) {
            console.error('Quality metrics:', response.error);
          }

          metrics = response.data;
          if (metrics && mode === 'edit') {
            return QualityMetricsService.getQualityMetricsScore(cid)
              .then(response => {
                angular.forEach(response.data, qmScore => {
                  const metric = metrics.find(({ id }) => id === qmScore.id);
                  metric.score = qmScore.score;
                });

                return metrics;
              });
        } else {
          return metrics;
        }
      });
    }

    const generateReportItemsFromTrackpoints = (trackpoints) => {
      var reportItems = [];
      angular.forEach(trackpoints, function(trackpoint) {
        angular.forEach(trackpoint.eventTypes, function(isPresent, eventType) {
          if (isPresent == true) {
            reportItems.push({
              eventType,
              cid: trackpoint.cid,
              name: trackpoint.name,
              active: trackpoint.active,
              tailcode: trackpoint.tailcode,
              properties: trackpoint.properties,
              evid: `${eventType}:${trackpoint.cid}:${trackpoint.tailcode}`,
            })
          }
        })
      });

      return reportItems;
    }

    /* ****************************************************** */
    /* ********************** Snapshots ********************* */
    /* *************************** ****************************/
    let snapshotsNew = [];
    let snapshotsForDelete = [];
    let isTechedgeNotificationHidden = false;
    const canSaveSnapshots = () => snapshotsNew.length !== 0 || snapshotsForDelete.length !== 0;
    const getNewSnapshots = () => snapshotsNew;
    const addSnapshot = snapshot => {
      snapshotsNew = [
        ...snapshotsNew,
        snapshot,
      ];
    };

    const saveNewSnapshots = pid => {
      if (snapshotsNew.length === 0) {
        return Promise.resolve([]);
      }

      const getValidSources = (sources = []) => (
        sources.map(source => _.omit({
          ...source,
          provider: source.platform,
        }, 'platform'))
      );

      return CampaignService.saveCampaignSnapshots({
        pid,
        snapshots: {
          snapshots: snapshotsNew.map(snapshot => ({
            name: snapshot.name,
            brand: snapshot.brand,
            audiences: snapshot.audiences,
            advertiserName: snapshot.advertiserName,
            startDate: snapshot.startDate,
            endDate: snapshot.endDate,
            eventType: snapshot.eventType,
            sources: getValidSources(snapshot.sources),
            reportItems: snapshot.reportItems,
            barbStandard: snapshot.barbStandard,
          })),
        },
      }).$promise
      .then(() => {
        snapshotsNew = [];
      });
    }

    const removeSnapshot = snapshot => {
      if (snapshot.removable) {
        snapshotsNew = snapshotsNew.filter(({ id }) => snapshot.id !== id); // remove just added snapshot
      } else {
        snapshotsForDelete.push(snapshot.id); // remove saved snapshot
      }
    };

    const deleteSnapshots = pid => {
      if (snapshotsForDelete.length === 0) {
        return Promise.resolve([]);
      }

      return CampaignService.deleteCampaignSnapshots({
        pid,
        snapshots: {
          snapshots: snapshotsForDelete.map(id => ({ id })),
        },
      }).$promise
      .then(() => {
        snapshotsForDelete = [];
      });
    }

    const showTechedgeNotification = campaign => {
      if (!campaign || !campaign.hasSnapshots) {
        return false;
      }

      const keyStorage = `hideTechedgeNotification${campaign.pid}`;
      const hideNotification = JSON.parse(localStorage.getItem(keyStorage)) || isTechedgeNotificationHidden;
      if (hideNotification) {
        return false;
      }

      ModalService.showModal({
        templateUrl: `/assets/js/app/components/techEdge/techEdge.notification.html?v=${window.BUILD_VERSION}`,
        controller: 'TechEdgeNotificationController',
        controllerAs: 'techEdgeNotification',
        inputs: {
          campaign,
        }
      }).then(modal => {
        modal.element.modal();
        // modal.close.then(result => {});
      });
    }

    const hideTechedgeNotification = () => {
      isTechedgeNotificationHidden = true;
    }



    /* ****************************************************** */
    /* ************************* DMS ************************ */
    /* *************************** ****************************/
    const cloneRoute = /\/(((create)(\/(\d*))?(\/(sameTrackpoints|newTrackpoints))?)\/dms)/;

    let operationsDms = {};
    const canSendToDms = () => (
      operationsDms.enabled &&
      operationsDms.DMSName &&
      operationsDms.advertiserName &&
      operationsDms.email && $filter('isValidEmail')(operationsDms.email) &&
      operationsDms.channels.length !== 0 &&
      (!operationsDms.created || cloneRoute.test($location.path()))
    );
    const getDms = () => operationsDms;
    const updateDms = dms => operationsDms = dms;
    const saveDms = pid => (
      CampaignService.saveCampaignOptionsDms({
        pid,
        dms: {
          pid,
          ...operationsDms,
        },
      }).$promise
    );



    /* ****************************************************** */
    /* ************************ Save ************************ */
    /* *************************** ****************************/
    const transformCampaign = campaign => {
      const { type, sources } = campaign;
      if (type === 'integrated') {
        return {
          ...campaign,
          sources: sources.map(item => ({
            ...item,
            provider: item.platform,
          })),
        };
      }

      return campaign;
    };

    let integrationsIndicator = {
      sources: [],
    };

    let mixedTrackpoints = [];
    const setMixedTrackpoints = trackpoints => { mixedTrackpoints = trackpoints; };
    const getNewTrackpointsAddedAmount = () => mixedTrackpoints.filter(item => item.isNew).length;

    let newTrackpointsAddedNotification = false;
    const showNewTrackpointsAddedNotification = show => { newTrackpointsAddedNotification = show; };
    const getNewTrackpointsAddedNotificationStatus = () => newTrackpointsAddedNotification;

    return {
      labelsPromise,
      countriesPromise,
      customSegmentsPromise,
      availableQualityMetrics,
      addSnapshot,
      removeSnapshot,
      deleteSnapshots,
      getNewSnapshots,
      saveNewSnapshots,
      showTechedgeNotification,
      hideTechedgeNotification,
      setMixedTrackpoints,
      getNewTrackpointsAddedAmount,
      showNewTrackpointsAddedNotification,
      getNewTrackpointsAddedNotificationStatus,
      canSaveSnapshots: () => canSaveSnapshots() && !!campaign.name && !!campaign.panel_country,
      getDms,
      saveDms,
      updateDms: dms => updateDms(dms),
      canSendToDms: () => canSendToDms(),
      getCampaignPromise: ($route) => {
        const id = $route.current.params.cid || window.location.pathname.split('/')[2]; // FIXME: use $route
        const generateNewTrackpoints = ($route.current.params.cloneStrategy == 'newTrackpoints');
        return getCampaignPromise(id, generateNewTrackpoints);
      },
      getCampaignData: () => campaign,
      setCampaignData: (newCampaignData) => { campaign = newCampaignData; },
      updateCampaign: () => {
        campaign.ris = generateReportItemsFromTrackpoints(campaign.trackpoints);
        return CampaignService.update(transformCampaign(campaign)).$promise
          .then(response => {
            if (response.error || response.data?.error) {
              console.error(response.error?.ex || response.data?.error?.ex)
            }

            return response;
          });
      },
      createCampaign: () => {
        campaign.ris = generateReportItemsFromTrackpoints(campaign.trackpoints);
        return CampaignService.save(transformCampaign(campaign)).$promise;
      },
      canTouchCampaign: () => isCampaignChanged() && campaign.name && campaign.panel_country,
      isCampaignFieldChanged: field => isCampaignFieldChanged(field),
      dirtyCriteria: () => getDirtyCriteria(),
      dirtyTargets: () => getDirtyTargets(),
      setIntegrationsIndicator: sources => integrationsIndicator = { sources },
      getIntegrationsIndicator: () => integrationsIndicator,
    };
  }
})();
